<template>
    <!-- Full DOM shape painted from page-load — `.complete-wrap` is
         always mounted so the LCP candidate paints at FCP. Per-state
         visibility is CSS-only via `data-engine-state`. `data-complete`
         remains bound for the dial-out choreography.

         AK-W2-β2 — the `<InstrumentChassis>` shell that previously
         wrapped this view hoisted to `src/App.vue` (the App-level
         chassis spine). The chassis now persists across the
         `speedtest → survey → thankyou` route transitions; this view
         slots into the chassis `#dial` via the RouterView. The
         `data-phase` cascade owner (`--phase-color` +
         `--phase-color-label`) lives at the App-level chassis, derived
         from `useSpeedtestStore().currentPhase` via the
         `chassisPhase` computed; `tokens.css`'s `[data-phase]
         .text-metric-label` selector still paints the metric label
         through `var(--phase-color-label, ...)` because the chassis
         ancestor still emits `data-phase` on its root. The empty-slot
         suppression CSS retired at W2-β1 (glass-ui 3822f48) — the
         chassis primitive now slot-gates its `<header>` / `<footer>`
         / dividers on `$slots.strip` / `$slots.control`. -->
    <Card
        tier="quiet"
        as="section"
        class="results-card meter-card"
        :data-complete="choreography.dataComplete.value"
        :data-engine-state="hero.engineState.value"
        :data-morph-resolved="choreography.dataMorphResolved.value"
    >
        <!-- Completion announcement for assistive tech. The
                     idle→running→complete arc is conveyed visually by the
                     dial morph; a non-sighted user otherwise gets no signal
                     the test finished. An ALWAYS-mounted `sr-only` polite
                     status region (empty until complete) announces the
                     state change once on the completion edge — the live
                     region must pre-exist for the content-change to be
                     announced reliably. `sr-only` (clip + absolute) keeps it
                     out of layout, so zero CLS. The result magnitudes stay
                     navigable in `<ResultStack>` below. -->
        <div
            class="sr-only"
            role="status"
            aria-live="polite"
            aria-atomic="true"
        >
            {{ completionAnnouncement }}
        </div>
        <!-- AV.W16 (D11) — the in-card PaperBackdrop celebratory substrate is
                     REMOVED. The re-audit read its `complete`-state texture as a
                     SECOND competing radial layer behind the result stack (the
                     user: "giant radial item in the card", "radial specular issue
                     still extant") — a flooded register rivalling the routed
                     Aurora wash. With it gone the Aurora is the SINGLE flooded
                     register on running/complete and the result stack reads
                     against the card's clean chassis ground. (The prior AI.W4-M.2.5
                     "printed-paper celebration" cue is retired; the celebration
                     register now rides the headline + morph choreography, not a
                     decorative substrate.) -->
        <!-- AJ-W3-β — Meter cluster composition root. The dial,
                     the under-meter `.phase-progress` cap (per-phase
                     0→100), and the global `<PhaseTimeline>` (overall
                     cross-phase progress) read as ONE instrument-cap
                     cluster, not as two grid-rows split by a `margin-
                     block-start: auto` floor-anchor. The cluster owns
                     its own vertical rhythm via `--progress-stack-gap`
                     (the W3-μ.3 canon). Per G-AJ-D6 + G-AJ-D8 ratified
                     dispositions; FD2 §6 DISCHARGE-5 + A3 §5.D-1 + D-2.
                     The composition root level here is the right home
                     for the cluster: the dial-out lane on
                     `[data-complete="true"]` still targets `.meter-col`
                     (the inner element); the cluster wrapper holds the
                     two children + the rhythm. -->
        <!-- D1 — AJ-W3-β REVERSAL (AN R2-core): PhaseTimeline is lifted OUT of
                         .meter-cluster to a card-level floor rail. The cluster now only owns
                         the dial + under-dial bar. PhaseTimeline below spans grid-column 1/-1
                         floor-affixed as the "instrument baseline rail."
                         CLS co-fix: the rail row is reserved in the grid-template-rows at all
                         states (grid-template-rows: 1fr auto) so the layout doesn't shift
                         when the rail's content gates in. -->
        <div class="meter-cluster">
            <!-- <MeterColumn> owns canvas + renderer + per-phase Progress
                         overlay + `.meter-col` wrapper. Reads phase identity directly
                         via useMetricRegistry() — each cell paints its colour from
                         MetricDef.color.

                         AN-T1 — `ref="meterColumn"` reaches the child whose
                         `defineExpose({ rootEl })` surfaces the real
                         `.meter-col` Element as the morph SOURCE (the
                         component is a fragment — its `$el` is a leading
                         comment anchor, see W2-β-FIX in the script); the
                         clock-owning `useCompletionChoreography` morphs
                         its rect into the `.complete-wrap` TARGET on the
                         completion edge AND gates the source eviction off
                         the morph's `.then()` so the source stays alive
                         through the 450ms morph window (the silent-cut
                         degrade fix).

                         T2 — :current-test-progress prop removed; MeterColumn
                         now store-derives its progress directly. -->
            <MeterColumn ref="meterColumn" />
        </div>

        <!-- D1 — Baseline rail at card floor (AN R2-core).
                     Instrument ground line: pre-announces all 4 phases at idle;
                     becomes the cross-phase map during running. Spans full card
                     width via grid-column 1/-1 (set in .baseline-rail below).
                     floor-affixed via margin-block-start: auto inside the card
                     flex/grid layout. CLS: height is always reserved (the row
                     exists at every state — visibility-gated, not display-none).

                     T2 — :current-test-progress prop removed; PhaseTimeline
                     now store-derives its progress directly. -->
        <PhaseTimeline class="baseline-rail" />

        <!-- Divider — horizontal at <720px, vertical at ≥720px -->
        <div class="results-divider" aria-hidden="true" />

        <!-- Metric column — container-query typography sizes off this width. -->
        <div class="metric-col">
            <div class="metric-display">
                <!-- Stack-superposition of the running pill-stack + the
                             complete-wrap shell at the same grid cell;
                             `data-engine-state` toggles visibility CSS-only.

                             `useHeroVariant` is the explicit state machine that
                             owns the dots/value/idle/complete projection — five
                             mutually-exclusive variants exhaust the
                             (engineState × hasData × activeMetric) space, so
                             adding a future branch is a discriminated-union
                             addition at the composable rather than ad-hoc v-if
                             drift here. -->
                <Transition name="metric-swap" mode="out-in">
                    <!-- Loading dots — items-start so the dots state doesn't
                                 re-centre while the value state is left-aligned;
                                 skeleton + loading + active value all anchor to
                                 the same left edge.

                                 The `.text-metric-label` colour binds through the
                                 chassis cascade (`[data-phase] .text-metric-label`
                                 → `var(--phase-color-label, ...)`) — no inline
                                 `:style="{ color: ... }"` so the cascade owns the
                                 label hue on ONE path. -->
                    <div
                        v-if="
                            hero.isRunningNoData.value &&
                            hero.activeMetric.value
                        "
                        :key="`dots-${hero.activeMetric.value.id}`"
                        class="metric-pill-stack flex w-full flex-col
                            items-center gap-2"
                    >
                        <div class="text-metric-label">
                            {{ hero.activeMetric.value.label }}
                        </div>
                        <Pulse :count="3" variant="dots" />
                    </div>

                    <!-- Active value -->
                    <div
                        v-else-if="
                            hero.isRunningWithData.value &&
                            hero.activeMetric.value
                        "
                        :key="`value-${hero.activeMetric.value.id}`"
                        class="metric-pill-stack flex w-full flex-col
                            items-center"
                    >
                        <div class="text-metric-label">
                            {{ hero.activeMetric.value.label }}
                        </div>
                        <!-- Hero binds the canonical `formatted` + `unit`
                                     path (same as ResultStack rows). Single
                                     source of truth: both flow from the
                                     per-metric bundle's single smoother + single
                                     ladder pass, so the hero and rows can't
                                     diverge by a unit rung. -->
                        <div
                            class="flex max-w-full min-w-0 items-baseline gap-2"
                            :style="{
                                '--digit-count':
                                    hero.results[hero.activeMetric.value.id]
                                        .formatted.value.length,
                            }"
                        >
                            <span class="text-hero tabular-nums">{{
                                hero.results[hero.activeMetric.value.id]
                                    .formatted.value
                            }}</span>
                            <span
                                class="text-subheading text-muted-foreground
                                    shrink-0 font-mono font-medium italic"
                            >
                                {{
                                    hero.results[hero.activeMetric.value.id]
                                        .unit.value
                                }}
                            </span>
                        </div>
                    </div>

                    <!-- Idle placeholder. Pre-engine, paints the same
                                 label / hero / unit shape as the active branch
                                 so the running-state value swap relayouts
                                 nothing. A subtle breathing aura on the
                                 em-dash hints the test is ready to start; the
                                 aura reads as a quiet halo, not a CTA.

                                 AJ-W3-ν R-2: hero glyph is the em-dash (the
                                 codebase's canonical "no value" placeholder,
                                 shared with `formatMetricValue(null)`) rather
                                 than the literal `0` the pre-fix branch
                                 painted. The pre-fix `0` read as "the test
                                 measured zero latency" instead of "the test
                                 is ready" per A3 §6 R-2. The em-dash is the
                                 same rendering the rail pills use at idle, so
                                 the mobile idle composition reads as
                                 single-register "ready" rather than two
                                 disambiguation modes.

                                 The `idle` variant covers both pre-engine AND
                                 `aborted` (post-running drop without complete);
                                 `isAborted` disambiguates the two for callers
                                 that want to fade the meter to its idle posture
                                 cleanly.

                                 AG-D1 / A3§P1 Option 2 — at DESKTOP idle
                                 (≥720px) the whole `.metric-col` collapses
                                 (`opacity` + `visibility` at the composition
                                 root) so the dial is the protagonist; this
                                 placeholder is the metric column's MOBILE idle
                                 readout (the mobile flex stack keeps the dial +
                                 "0 ms" island, which is the correct mobile
                                 composition). On desktop the dial canvas is the
                                 idle paint; the metric column opens on the
                                 idle→running reveal. -->
                    <div
                        v-else-if="
                            (hero.isIdle.value || hero.isAborted.value) &&
                            hero.idlePlaceholder.value
                        "
                        :key="`idle-${hero.idlePlaceholder.value.id}`"
                        class="metric-pill-stack flex w-full flex-col
                            items-center"
                        aria-hidden="true"
                        data-lcp-placeholder="true"
                    >
                        <div class="text-metric-label">
                            {{ hero.idlePlaceholder.value.label }}
                        </div>
                        <div
                            class="idle-hero-host flex max-w-full min-w-0
                                items-baseline gap-2"
                            :style="{ '--digit-count': 1 }"
                        >
                            <Pulse
                                variant="aura"
                                intensity="subtle"
                                class="idle-hero-aura"
                                aria-hidden="true"
                            />
                            <span class="text-hero relative tabular-nums"
                                >—</span
                            >
                            <span
                                class="text-subheading text-muted-foreground
                                    relative shrink-0 font-mono font-medium
                                    italic"
                            >
                                {{ hero.idlePlaceholder.value.defaultUnit }}
                            </span>
                        </div>
                    </div>

                    <!-- Blank (final fallback — variant=running-no-data
                                 without an active metric; no LCP-stable shape to
                                 paint, no value to surface). -->
                    <div v-else key="blank" class="metric-pill-stack" />
                </Transition>

                <!-- Complete-wrap shell always mounted; CSS-only visibility
                             via `[data-engine-state="complete"]`. The completion
                             choreography is the AN-T1 one-clock-owner sequence:
                             the morph SPINE delivers the box (`.meter-col` →
                             `.complete-wrap`) over 450ms on `--spring-bouncy`;
                             when the morph resolves (`data-morph-resolved="true"`),
                             the headline cluster + result rows REVEAL in-place
                             on `--spring-snappy`. No pane-slide layered on top
                             of the morph — the morph IS the entrance.

                             The headline cluster is unconditionally mounted at
                             complete (CSS controls visibility); the reveal lanes
                             below (gated on `data-morph-resolved`) supply the
                             fade-up off the delivered box. The AJ-W2-δ
                             `<Transition name="pane-slide">` retired here as
                             the redundant second channel (per AN-design-v2
                             SPEC §1.3.3) — the morph + the in-place spring-
                             snappy reveal are the single coherent transition.
                             `pane-slide` stays the canonical route/survey-step
                             transition vocabulary elsewhere. -->
                <div
                    ref="completeWrap"
                    class="complete-wrap"
                    :data-engine-state="hero.engineState.value"
                >
                    <!-- AV.W9.a — the golden mark + "Complete!" lockup
                                 carved into `<ResultHeadlineCluster>` behind a
                                 single `:shown` boundary. The `complete-reveal`
                                 reveal gate still reaches its root via the
                                 parent's scoped `data-v-*` stamp on the child
                                 root — the choreography gate stays at this
                                 composition root (`data-morph-resolved`). -->
                    <!-- AV.W19 hidden-celebration fix: `:shown` MOUNTS the
                         cluster on the completion edge (headline in the DOM
                         at complete); `:play-enter` keys the seal's staged
                         enter on the REVEAL clock (`revealActive` =
                         `data-morph-resolved`), so the ~840ms disc / ring /
                         check draw plays WHEN the reveal opens its
                         `opacity 0 → 1` — the celebration is the WITNESSED
                         climax, not a beat played under the hidden container. -->
                    <ResultHeadlineCluster
                        :shown="hero.isComplete.value"
                        :play-enter="choreography.revealActive.value"
                        :personal-best="isPersonalBest"
                    />
                    <!-- AN-D5 — the divider retired from this seat;
                                 the brand `divider-h-tapered` now lives
                                 INSIDE `<ResultStack>` between the
                                 PRIMARY (DL+UL) and SECONDARY (latency+
                                 jitter) sections — the real hierarchy
                                 break. -->
                    <ResultStack
                        class="complete-reveal complete-reveal--rows"
                    />
                </div>
            </div>
        </div>
    </Card>
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef, watch } from "vue";
import { Card } from "@mkbabb/glass-ui/card";
import MeterColumn from "./MeterColumn.vue";
import ResultStack from "./ResultStack.vue";
import PhaseTimeline from "./PhaseTimeline.vue";
import ResultHeadlineCluster from "./ResultHeadlineCluster.vue";
import { Pulse } from "@mkbabb/glass-ui/pulse";
import { useHeroVariant } from "@features/speedtest/composables/useHeroVariant.ts";
import { useCompletionChoreography } from "@features/speedtest/composables/useCompletionChoreography.ts";
import { recordRun } from "@features/speedtest/state/personal-best.ts";

// T2 — currentTestProgress prop removed; the forwarding chain
// (TraditionalSpeedtest → SpeedtestResults → MeterColumn/PhaseTimeline) is deleted.
// MeterColumn and PhaseTimeline now store-derive their progress directly from
// useSpeedtestStore(). SpeedtestResults has no remaining prop surface.

// AN-T1 — `useCompletionChoreography` is now the clock-owner (the
// structural twin of `usePhaseTransition`). One clock — the morph —
// drives the SOURCE eviction + the REVEAL cascade off ONE rising
// edge. The composable is wired with template refs to the morph
// SOURCE (the `.meter-col` element MeterColumn exposes via
// `defineExpose({ rootEl })` — fragment-anchor safe per the W2-β-FIX
// guard inside the composable) and TARGET (the `.complete-wrap`
// div). Returns:
//   - `dataComplete`        — flips IMMEDIATELY on the rising edge;
//                              seats the morph TARGET via
//                              `[data-engine-state="complete"]`.
//   - `dataMorphResolved`   — DEFERRED to the morph's `.then()`;
//                              gates the SOURCE `.meter-col { display:
//                              none }` eviction (so the source stays
//                              ALIVE through the morph window) AND
//                              the in-place reveal cascade.
//
// The retired `useContainerMorph` seam folded INTO this composable —
// the morph spine, the iOS bouncy spring, the fragment-anchor +
// degenerate-geometry safety, and the decorative-failure invariant all
// live in the clock-owner now (no second composable, no second clock).
const meterColumn = useTemplateRef<{ rootEl: HTMLElement | null }>(
    "meterColumn",
);
const completeWrap = useTemplateRef<HTMLElement>("completeWrap");
const choreography = useCompletionChoreography({
    source: computed(() => meterColumn.value?.rootEl ?? null),
    target: completeWrap,
});

// Hero pill state flows from `useHeroVariant()` — it owns the
// `useMetricResults()` map (one rAF smoother fan-out), the active-metric
// resolution, and the LCP-stable idle-placeholder lookup, so the hero
// consumer site never re-instantiates the smoother graph the rows
// already share.
const hero = useHeroVariant();

// B2 — personal-best slot (folded into W22.1). On the completion rising
// edge `useHeroVariant` already owns (`isComplete` false→true), record the
// run's throughput against the device's stored best and latch the verdict.
// The clock is W19's (`useCompletionChoreography`); this watch only reads
// the edge it publishes and writes the tiny `localStorage` slot — it never
// touches the choreography. `isPersonalBest` flows to the seal so the
// `<CompleteBadge>` sweeps its gold-arc flourish when the download
// protagonist beat the prior best.
const isPersonalBest = ref(false);
watch(
    hero.isComplete,
    (now, prev) => {
        if (now && !prev) {
            const verdict = recordRun({
                download: hero.results.download.raw.value ?? 0,
                upload: hero.results.upload.raw.value ?? 0,
            });
            isPersonalBest.value = verdict.isBest;
        } else if (!now) {
            // Reset for the next run (a fresh idle → run → complete cycle
            // re-derives the verdict from scratch).
            isPersonalBest.value = false;
        }
    },
    { immediate: true },
);

// Completion announcement for the `sr-only` polite status region. Empty
// until the engine reaches `complete`, then a concise state-change line —
// the region pre-exists (always mounted) so the empty→text change is what
// the screen reader announces. The per-metric magnitudes stay navigable in
// `<ResultStack>`, so the announcement confirms the state, not the numbers.
// A personal-best run appends the triumph so the gold-arc flourish has a
// screen-reader companion (the arc itself is decorative/aria-hidden).
const completionAnnouncement = computed(() => {
    if (!hero.isComplete.value) return "";
    return isPersonalBest.value
        ? "Speed test complete. New personal best download."
        : "Speed test complete.";
});

// AK-W2-β2 — chassis-phase binding lives at `src/App.vue`. The
// `<InstrumentChassis>` that previously wrapped this view hoisted to
// the App-level chassis spine; the App's `chassisPhase` computed
// reads `useSpeedtestStore().currentPhase` on the speedtest route, so
// the cascade owner still drives `--phase-color` + `--phase-color-label`
// across the chassis — this view no longer needs to import the store
// or pass `:phase` locally. The `[data-phase] .text-metric-label`
// selector at `tokens.css` still resolves because the chassis ancestor
// continues to emit `data-phase` on its root.
</script>

<!-- AV.W9.a — the non-scoped metric-swap re-time style domain
     (AG-A4-METRIC-SWAP-RETIME) carved into a colocated stylesheet
     (`./styles/result-metric-swap.css`) and re-attached via `<style
     src>` WITHOUT `scoped` so the emitted CSS stays byte-identical to
     the prior inline non-scoped block. It re-times the GLOBAL glass-ui
     `metric-swap-*` classes scoped to the `.metric-display` host this
     view owns — a self-contained style domain with no `data-v-*`
     dependency, so it lives outside the SFC cleanly. -->
<style src="./styles/result-metric-swap.css"></style>

<!-- AV.W19 — the route-axis View-Transition curves for the running↔result
     surface (the `retake` reverse direction). NON-SCOPED because
     `::view-transition-*` are document-level pseudos no `data-v-*` scope can
     reach; `view-transition-name: speedtest-surface` lives in the scoped block
     below on `.results-card`. DDR-AV-ROUTE + DDR-AV-EASING. -->
<style src="./styles/result-view-transition.css"></style>

<style scoped>
/* AK-W2-β2 — the `.results-chassis` scoped block (chassis transparency
   + `::before` bezel retirement) hoisted to `src/App.vue`'s
   `.app-chassis-spine` host. The card here still clamps to
   `var(--chassis-max-block-size)` so it sits inside the chassis
   envelope cleanly; the chassis is the ancestor surface but paints
   transparent so the inner Card reads as the single surface element. */

/* AV.W16 (D11) — the `.results-paper-backdrop` rule is REMOVED with its
   `<PaperBackdrop>` element (the in-card radial that competed with the
   routed Aurora). The card keeps `position: relative` below for the
   `.complete-wrap` morph-target stacking, NOT for a backdrop sibling. */

.results-card {
    width: 100%;
    max-width: min(1400px, calc(100% - 2rem));
    margin: 0 auto;
    /* AM-W7 — reconcile to the glass-ui Card padding canon. The Card
       family (`CardContent` / `CardHeader` / `CardFooter`) all pad at
       `p-6` = 1.5rem; an AK refactor drifted this consumer to a
       `clamp(0.75rem, 1.5vw, 1.5rem)` that bottomed out far below the
       canon on mobile. Match the flat 1.5rem inset so the results card
       reads as the same surface register as every other Card; the
       `overflow: clip` + `max-block-size` cap below absorb the slightly
       tighter content lane. Radius reconciles to `--radius-card` (the
       `--radius-2xl` = 1rem canon every glass surface — InstrumentChassis,
       dock, rail — already rounds to) so the corner matches the family. */
    padding: 1.5rem;
    border-radius: var(--radius-card);
    /* D1 — AN R2-core: the card is a 2-row flex column.
       Row 1: the main content area (meter-cluster + divider + metric-col);
       Row 2: the baseline rail (grid-column: 1/-1 at desktop; full-width
       at mobile via align-self: stretch). The rail's row height is always
       reserved (CLS co-fix) — content-gated via opacity/visibility, NOT
       display:none, so the layout band never collapses and reflows. */
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: clamp(0.5rem, 1vw, 1rem);
    /* The card declares `position: relative` as the local stacking +
       positioning anchor for its absolutely-positioned descendants (the
       `.complete-wrap` morph target pins against `.metric-display`, but the
       card-level relative keeps the result stack's stacking context local
       and self-contained). The Card primitive already declares relative
       positioning; the explicit hint here keeps the anchor obvious at the
       consumer site. (AV.W16 D11: the former `.results-paper-backdrop`
       sibling this also anchored is removed — the routed Aurora is the
       single flooded register.) */
    position: relative;
    /* AV.W19 — the route-axis View-Transition NAME. The retake reverse
       transition (`useRouteTransition().navigate(..., { types:["retake"] })`
       in `useAppNavigation`) wraps the complete→running route swap in
       `startViewTransition`; this stable `view-transition-name` makes the
       card the captured morph element so the old (complete) → new (running)
       swap morphs in place under the directional `:active-view-transition-
       type(retake)` curve (`./styles/result-view-transition.css`) instead of
       teleporting. ONE motion owner per axis: this names the ROUTE/state-flip
       surface; the in-component `ElementMorph` (DDR-AV-W19-MORPH-FIDELITY)
       owns the meter→number geometric morph — distinct names, no collision.
       Feature-detected: a non-supporting engine never generates the
       `::view-transition*` pseudo tree, so the name is inert there. */
    view-transition-name: speedtest-surface;
    /* Fill the dock-adjusted viewport: both min and max clamp against the
       chassis budget so the card claims the full available area (top and
       bottom) rather than shrink-wrapping to content. The chassis budget
       already subtracts dock + footer + edge-inset — what remains is the
       card's space and it should be fully occupied. */
    min-block-size: var(--chassis-max-block-size);
    max-block-size: var(--chassis-max-block-size);
    overflow: clip;
    /* AP-Pγ — establish an inline-size container so the idle
       dial-as-protagonist centring transform (`@media ≥720px` below)
       can read the card's content width via `100cqw` and offset the
       dial to card-centre WITHOUT a grid-placement re-seat (the prior
       ~0.10 CLS source on `/`). `inline-size` contains only the inline
       axis — the card's explicit `min/max-block-size` + flex/grid
       block layout are untouched. Inner container queries (`.metric-col`
       carries its own `container-type: inline-size`; `.text-hero`'s
       `45cqi`, the `@container (min-width: 28rem)` headline query, and
       the `complete-wrap` `cqi` gap all resolve against that nearer
       container) are unaffected — this card-level container is a new
       OUTER context that only the cluster-centring transform reads. */
    container-type: inline-size;
}

/* `.meter-col` local-appearance rules live on MeterColumn. The
   cross-cell layout + dial-out lanes here still match the child
   because Vue scoped CSS stamps the child's root with the parent's
   `data-v-*` attribute. */

/* `container-type: inline-size` on `.metric-col` enables container-
   query sizing so `.text-hero` and `.results-stack` rows scale off
   this width. Empty card space comes from gap + state-specific row
   reservation, not from padding here; the meter-display state budgets
   below do the reservation. align-items + text-align stay flex-start /
   left across every state. */
.metric-col {
    container-type: inline-size;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    text-align: left;
    gap: 0.625rem;
}

/* `.metric-display` stack-superimposes pill-stack + complete-wrap at
   the same grid cell; `data-engine-state` toggles visibility CSS-only.

   The state budget reserves space for one hero line + unit + label
   (~9rem floor, 16rem ceiling). The complete state owns its own row
   reservation via `.complete-wrap` so it doesn't need a budget here. */
.metric-display {
    position: relative;
    width: 100%;
    display: grid;
    grid-template-areas: "stack";
    align-items: center;
    justify-content: stretch;
    /* Reserve enough space for one hero line + label + unit so swapping
       across the active-pill / dots / idle / complete states doesn't
       relayout the chassis.

       AP-Pγ CLS fix (RCA: A6 Finding 3, secondary source) — the prior
       reservation `clamp(4.5rem, 12cqi + 2rem, 9rem)` peaked at 9rem
       (~144px), but the RUNNING pill-stack (hero `text-hero` number +
       label) renders ~300px on a wide container — so when the first
       live value populated, `.metric-display` grew 123→300px and the
       vertically-centred metric column re-laid-out (`dy=-88 dh=+177`,
       the second-largest shift on `/`). The reservation must track the
       SAME scale the hero clamp uses so the running value lands inside
       the pre-reserved box.

       The hero is `clamp(5rem, 45cqi·3/max(3,digits), …)` ≈ 45cqi at
       the ≤3-digit default; the pill-stack adds a ~2.5rem label + gap on
       top. The reservation mirrors that: `40cqi` reserves the hero's
       rendered height band (line-height-capped, < the 45cqi font size)
       plus the label, floored at 7.5rem (the 5rem hero floor + label so
       a tiny container still pre-reserves the running content) and
       ceiled at 20rem so a very wide container doesn't reserve runaway
       air. The container is `.metric-col` (own `container-type:
       inline-size`), so the reservation scales WITH the hero across
       every viewport — no fixed magic height. */
    min-height: clamp(7.5rem, 40cqi, 20rem);
}
/* Drop the active-pill budget at complete so rows + headline compose
   naturally without the pre-allocation pad. */
.results-card[data-engine-state="complete"] .metric-display {
    min-height: 0;
}
.metric-display > .metric-pill-stack,
.metric-display > .complete-wrap {
    grid-area: stack;
}

/* Pill-stack hides at complete; complete-wrap stays painted at FCP (LCP
   candidate). The visibility transition delays `visibility: hidden`
   until the opacity fade-out completes so Retake → idle leaves cleanly.

   When NOT complete, complete-wrap is `position: absolute` so it drops
   out of `.metric-display` height flow but stays painted (LCP). The
   `overflow: clip` keeps the painted geometry inside the absolute inset
   so `card.scrollHeight` stays bounded to the chassis cap, and short
   viewports don't see the inner `.results-stack` min-block-size escape
   past the chassis-clamp budget. */
.results-card[data-engine-state="complete"] .metric-pill-stack {
    display: none;
}
.results-card .complete-wrap {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    position: absolute;
    inset: 0;
    overflow: clip;
}
.results-card[data-engine-state="complete"] .complete-wrap {
    position: static;
    overflow: visible;
}
.results-card[data-engine-state="complete"] .complete-wrap {
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
    transition:
        opacity var(--duration-normal) var(--ease-out),
        visibility 0s linear 0s;
}
.results-card:not([data-engine-state="complete"]) .complete-wrap {
    transition:
        opacity var(--duration-normal) var(--ease-out),
        visibility 0s linear var(--duration-normal);
}

/* Hero clamp consumes glass-ui's `.text-hero` utility — `--text-hero-size`,
   `--text-hero-leading`, `--text-hero-tracking` are knobs the utility
   exposes. The `45cqi * 3 / max(3, digits)` ramp keeps the hero at its
   poster-type register on mobile (~140px at 320px container) and tapers
   only as digit-count climbs past 3. Ceiling pegs at
   `--type-display-audacious` (peak 352px — fast.com peg).

   AJ-W1-γ — running-hero VALUE register cascade. The `.text-hero` slot
   carries the value's typographic register; the chart-color contract
   binds the colour to `--phase-color` (the registered, cross-fading
   channel set by `<InstrumentChassis>` per `tokens.css:451-459`). The
   binding is explicit at the leaf so the resolved rung is invariant to
   any upstream cascade-layer race. Under idle / complete the channel
   resolves to its `@property` initial-value `#888` (matching
   `--muted-foreground`), so the rest state stays neutral — only the
   active running phase tints the hero number saturated. Canon per
   DESIGN.md §1387-1389 + FD1 §1.2 + FD2 §3.2.1; G-AJ-D4 holds the
   chart-download = Tesla Ultra Red / chart-upload = amber canon.

   Refs P1-2 (regression-screenshots/meter-with-progress-jitter.png) —
   pre-fix VALUE painted `--foreground` (black); post-fix paints
   `--phase-color` saturated. */
.metric-display .text-hero {
    --digit-count: 3;
    --text-hero-size: clamp(
        5rem,
        calc(45cqi * 3 / max(3, var(--digit-count))),
        var(--type-display-audacious)
    );
    color: var(--phase-color);
}

/* AJ-W1-γ — running-hero LABEL register cascade. The chassis-side
   cascade at `tokens.css:413-415` already paints `.text-metric-label`
   via `var(--phase-color-label, ...)` (the desaturated WCAG companion
   rung). The defensive binding here is bound to the speedtest consumer
   scope so it wins any cascade-layer race with the publisher default,
   AND it explicitly refers to `--phase-color-label` so the upstream
   cascade order doesn't change the resolved rung. If
   `--phase-color-label` is unset (idle / complete phase), the fallback
   chain reaches `--muted-foreground` — the rest grey. Canon per
   DESIGN.md §1387-1389. */
.metric-display .text-metric-label {
    color: var(--phase-color-label, var(--muted-foreground));
}

/* Divider — horizontal at <720px, vertical at ≥720px (set in the grid
   block below). Opacity fades during the dial-out crossfade. */
.results-divider {
    width: 100%;
    height: 1px;
    background: color-mix(in srgb, var(--foreground) 12%, transparent);
    flex-shrink: 0;
    opacity: 1;
    transition: opacity var(--duration-normal) var(--ease-out);
}

/* AJ-W3-β — Meter cluster composition root. The dial + under-meter
   bar + global timeline compose as ONE cluster (`.meter-cluster`); per
   FD2 §6 DISCHARGE-5 the cluster owns its own internal vertical rhythm
   via `--progress-stack-gap` (the W3-μ.3 canon). The cluster siblings
   MeterColumn + PhaseTimeline as a flex column, retiring the former
   grid-row-2 `align-self: end` floor-anchor on `.timeline-wrap`. The
   `.timeline-wrap`'s own `margin-block-start: auto` rule (kept at
   PhaseTimeline.vue:208) is now a no-op inside this flex column (no
   spare space to push against). */
.meter-cluster {
    display: flex;
    flex-direction: column;
    gap: var(--progress-stack-gap);
    width: 100%;
    min-width: 0;
}

/* D1 — AN R2-core: baseline rail floor-affixed at the card bottom.
   The .timeline-wrap inside PhaseTimeline already carries `margin-block-start: auto`
   which floor-anchors it when it is the last flex child of a flex column with spare
   space. Now that PhaseTimeline is a direct card-level child (lifted out of
   .meter-cluster), the card flex column provides the spare vertical space and the
   auto margin seats the rail on the chassis floor — the instrument ground line.

   At mobile (flex column): width: 100% (inherits from align-items: stretch on
   the card), naturally at the bottom of the flex column.
   At desktop: grid-column: 1 / -1 so the rail spans ALL THREE grid tracks
   (meter / divider / metric), giving the full card-interior width.

   CLS co-fix: the rail uses opacity/visibility gating (never display:none) so
   its block-size is always reserved in layout — the running→idle→running cycle
   never causes a height reflow.

   Padding: inherit the card's padding-inline inset (0 here — the card's
   padding-inline is applied to the card itself). The .timeline-region inside
   already carries padding-inline: 0.5rem for the AE.W1 rivet-overhang reserve. */
.baseline-rail {
    width: 100%;
    /* margin-block-start: auto pushes the rail to the card floor in both
       flex and grid contexts when there is spare vertical space. */
    margin-block-start: auto;
}

/* ≥720px grid: explicit-sized trio (meter-cluster / divider / metric).
   The template is explicit (not `auto auto auto`) because `width: 100%`
   children + auto tracks resolve to a circular dependency and collapse
   to 0px tracks. Explicit sizing breaks the circle:
     - meter-cluster cell pins to `var(--meter-size)` directly
     - divider stays auto-sized to its 1px content
     - metric cell takes the remainder via 1fr with min:0 guard.

   AJ-W3-β — post-nest the global timeline lives INSIDE `.meter-cluster`
   (not as a separate grid-row). The grid is now a single ROW; the
   timeline retires from the grid level entirely. The dial-out
   choreography still targets `.meter-col` (the inner element of the
   cluster), so the slide reads unchanged.

   AG-D1 / A3§P1 Option 2 — the grid-template-columns above is the SINGLE
   track definition for idle AND running; it never changes across the
   states, so the idle→running transition has zero horizontal track
   reflow (no CLS). What changes per state is purely cell PLACEMENT +
   per-cell visibility:
     - running/complete: the meter-cluster sits in track 1, the divider
       in track 2, the metric column in track 3 (the trio below).
     - idle: the meter-cluster spans `1 / -1` and centres (the
       protagonist posture); the divider, the metric column, and the
       timeline-INSIDE-the-cluster collapse via `opacity` + `visibility`
       — NOT `display: none`, which would let the metric cell's content
       stop contributing and re-introduce the very reflow Option 2 must
       avoid. The cells stay in the grid; they are just invisible and
       inert at idle.
   The `data-engine-state` attribute on `.results-card` is the single
   layout owner (`idle | running | complete`). */
@media (min-width: 720px) {
    .results-card {
        display: grid;
        /* D1 — AN R2-core: 2-row grid.
           Row 1 (1fr): the instrument trio (meter-cluster / divider / metric-col).
           Row 2 (auto): the baseline rail, spanning all three columns.
           The auto row always reserves the rail's block-size (CLS co-fix). */
        grid-template-columns: minmax(0, var(--meter-size)) auto minmax(0, 1fr);
        grid-template-rows: 1fr auto;
        justify-content: center;
        align-items: center;
        gap: clamp(1rem, 3vw, 3rem);
        /* AM-W7 — the ≥720 override also drifted above the Card canon
           (peaked at 2.25rem). The cross-cell `gap` already supplies the
           wide-viewport breathing between the meter / divider / metric
           tracks; the card INSET stays at the 1.5rem `p-6` canon so the
           padding register is viewport-invariant, exactly like the Card
           family. */
        padding: 1.5rem;
    }
    .meter-cluster {
        grid-column: 1;
        grid-row: 1;
    }
    .results-divider {
        grid-column: 2;
        grid-row: 1;
        width: 1px;
        height: 70%;
        align-self: center;
    }
    /* `padding-inline-start` supplies a breathing-room gutter so the
       value cluster doesn't sit hard against the divider on wide
       viewports. */
    .metric-col {
        grid-column: 3;
        grid-row: 1;
        align-items: flex-start;
        text-align: left;
        padding-inline-start: clamp(1rem, 2vw, 3rem);
    }
    .metric-display {
        justify-content: flex-start;
    }
    /* D1 — baseline rail spans all three columns at row 2, floor-affixed. */
    .baseline-rail {
        grid-column: 1 / -1;
        grid-row: 2;
        margin-block-start: 0; /* grid row handles placement; no auto-push needed */
        align-self: end;
    }

    /* ── AG-D1 idle composition — dial-as-protagonist ──────────────────
       At idle the dial is the whole instrument: visually centred in the
       card, the metric column collapses, and the timeline (inside the
       cluster) is suppressed.

       AP-Pγ CLS fix (RCA: A6 Finding 3) — the prior posture spanned the
       cluster `grid-column: 1 / -1` + `justify-self: center` at idle and
       re-seated it to `grid-column: 1` (track 1) at running. That
       re-seat MOVES the dial's box ~439px leftward on the idle→running
       transition (measured `dx=-439`, the dominant ~0.10 layout-shift
       source on `/`). A grid-PLACEMENT change still relocates the box,
       and `layout-shift` scores box relocations even when the grid
       TEMPLATE is unchanged — so the "placement-not-template" rationale
       did not actually avoid CLS.

       The gestalt fix: the cluster stays in `grid-column: 1` (its
       running track) at idle too — its LAYOUT box never moves. The
       dial-as-protagonist CENTRING is expressed as a `transform:
       translateX(...)` (a compositor property exempt from layout-shift
       scoring), so the idle→running re-seat is a pure transform
       interpolation back to `translateX(0)` with ZERO CLS. The offset
       is the half-width of everything to the right of track 1
       (`(card-content-width − dial-width) / 2`), read robustly from the
       card's own container size — `.results-card` is an inline-size
       container (below) so `100cqw` resolves to the card's content
       width and `var(--meter-size)` is the dial's track-1 width. No
       hard-coded magic number; the centring tracks the viewport.

       The wider `--meter-size-idle` budget still applies so the
       protagonist dial claims the idle size; the width transition
       (already on `.meter-col`) narrows it back to `--meter-size` while
       the transform re-seats it — both compositor/interpolated, no
       track reflow. */
    .results-card[data-engine-state="idle"] .meter-cluster {
        transform: translateX(calc((100cqw - var(--meter-size)) / 2));
    }
    /* The transform interpolates back to identity on the idle→running
       re-seat, riding the same slow `--ease-standard` exit/surface bezier the
       dial's width transition uses so the re-seat reads as one motion (the §6
       departure register — a re-seat must settle, not overshoot). */
    .results-card .meter-cluster {
        transform: translateX(0);
        transition: transform var(--duration-slow) var(--ease-standard);
        will-change: transform;
    }
    .results-card[data-engine-state="idle"] .meter-col {
        --meter-col-target: var(--meter-size-idle);
    }
    /* The divider + metric column stay in the grid (no `display: none`,
       no track collapse) but fade to invisible + inert at idle — the
       protagonist dial is alone on screen. The reveal to running fades
       them back in as the dial narrows into track 1. */
    .results-card[data-engine-state="idle"] .results-divider,
    .results-card[data-engine-state="idle"] .metric-col {
        opacity: 0;
        visibility: hidden;
        pointer-events: none;
        transition:
            opacity var(--duration-normal) var(--ease-out),
            visibility 0s linear var(--duration-normal);
    }
    /* The metric column + divider fade IN on the idle→running reveal.
       The fade-in is delayed past the dial's narrowing so the reveal
       reads as a sequence — the dial re-seats into track 1 first, then
       the metric column opens — rather than a simultaneous state-snap. */
    .results-card[data-engine-state="running"] .results-divider,
    .results-card[data-engine-state="running"] .metric-col {
        transition:
            opacity var(--duration-normal) var(--ease-out)
                var(--duration-normal),
            visibility 0s linear 0s;
    }
    /* D1 — the baseline rail at idle shows the pre-announced empty phases
       (the "instrument ground line" at zero fill). Per the AN CORE SPEC §2:
       "at rest it frames the dial without competing." We NO LONGER suppress
       it at idle — idle visibility is the FEATURE (pre-announce).
       PhaseTimeline's own [data-engine-state="complete"] retirement (in
       PhaseTimeline.vue) still fires on completion.

       CLS co-fix: because the rail is always visible at idle (not hidden),
       the layout band is always occupied and there is no height reflow
       when the run starts. This resolves the prior CLS-0.320 defect. */
}

/* AN-T1 — the SOURCE eviction is gated on the morph CLOCK's resolution,
   not on the state flip. The state flip (`data-engine-state="complete"`)
   fires IMMEDIATELY on the rising edge so the morph TARGET (`.complete-
   wrap` absolute→static) is seated for the rect-read; the morph SPINE
   then plays an inline transform on `.meter-col` (the SOURCE) over the
   450ms `--spring-bouncy` window. The CSS dial-out fallback below
   (`[data-complete="true"]` translateX + opacity) is the
   inert-path/PRM fallback only — the inline morph transform supersedes
   it during the morph window.

   `[data-morph-resolved="true"]` is the eviction gate: it flips after
   the morph promise settles (i.e. `progress === 1`) so the source is
   ALIVE for the entire morph window, then evicted on the morph's own
   resolution. This is the AN-T1 fix for the silent-cut degrade where
   the eviction ~40ms into the 450ms window dropped the morph SOURCE
   out from under the inline transform. PRM / missing-source / morph-
   failure all flip the resolved handle immediately, so the eviction
   still fires honestly on inert paths.

   The dial-out CSS lane stays as the visual FALLBACK on inert paths
   (PRM, jsdom mounts, the W2-β fragment-anchor regression). On the
   active morph path the inline transform overrides the translateX
   while the morph plays; once cleared (by the clock-owner's `.then()`),
   the morph-resolved attribute is already "true" and the source
   evicts. The result either way is a clean source farewell. */
.results-card[data-complete="true"] .meter-col {
    opacity: 0;
    transform: translateX(-24%);
    pointer-events: none;
}
.results-card[data-complete="true"] .results-divider {
    opacity: 0;
}
.results-card[data-morph-resolved="true"] .meter-col,
.results-card[data-morph-resolved="true"] .results-divider {
    /* Drop from layout once the morph clock has resolved — NOT on the
       state-flip tick. The clock-owner (useCompletionChoreography)
       flips `data-morph-resolved="true"` from inside the morph's
       `.then()`, so the source is held alive across the entire 450ms
       window and only evicts at the natural end of the spine. */
    display: none;
}

/* AQ-P3 — the morph SOURCE surface (`.meter-cluster`) + the floor rail
   (`.baseline-rail`) leave FLOW at complete so the results surface
   (`.metric-col → .complete-wrap`) is the SOLE in-flow child filling the
   fixed card box from the FIRST complete frame. This is the second half
   of the "one sized surface" idiom: not only is the card box fixed (the
   `min-block-size` reversal above), the swap of meter→results must not
   re-flow the metric column either.

   THE SHIFT THIS KILLS: the AN-T1 clock-owner holds the morph source
   (`.meter-col` inside `.meter-cluster`) ALIVE through the 450ms morph
   window, then evicts it via `[data-morph-resolved="true"] .meter-col
   { display: none }`. While the source was a SEQUENTIAL flex child of
   the complete-state column, it occupied the card's flex space and
   squeezed `.metric-col` to its content height (~76px) pinned at the
   card FLOOR; the moment the source evicted on morph-resolve, the
   metric column's `flex: 1 1 auto` claimed the freed space and JUMPED
   to full height + centred (`dy -501, dh +501` — a ~0.24 CLS spike,
   the new dominant shifter once the card box was fixed).

   Taking the meter-cluster + rail OUT of flow (absolute) at complete
   means the metric column is the only in-flow child at EVERY complete
   frame — it fills + centres in the fixed card immediately, and the
   source eviction frees only out-of-flow geometry (zero reflow). The
   meter-cluster stays `position: absolute` (NOT `display: none`) so its
   rect is still measurable on the completion rising edge — the morph
   reads `source.getBoundingClientRect()` before `data-morph-resolved`
   flips, so the iOS bouncy spine still plays from the meter's real
   resting box. The morph plays an inline transform on the (now
   absolutely-positioned) source; the CSS dial-out fallback + the
   eventual `display: none` eviction both still apply. */
.results-card[data-engine-state="complete"] .meter-cluster,
.results-card[data-engine-state="complete"] .baseline-rail {
    position: absolute;
    inset: 0;
    margin: 0;
}
/* The divider leaves flow too (it's already `opacity: 0` at complete) so
   the metric column is the SOLE in-flow child of the complete-state flex
   column — no inter-child `gap` contribution to fight the metric column's
   `flex: 1 1 auto` fill. Pinned (not inset-stretched) so its retired
   1px geometry stays out of the way. */
.results-card[data-engine-state="complete"] .results-divider {
    position: absolute;
    inset-block-start: 0;
    inset-inline: 0;
    margin: 0;
}

/* Grid → flex toggle at complete. The 3-track grid (meter / divider /
   metric) lives for idle + running where the trio composes around the
   meter; at complete the meter retires and the stack reads as a single
   full-width column. The metric-col drops its grid-column constraint
   so the inner `.complete-wrap` claims the card's full width — without
   this the rows would overflow past `max-block-size` and `overflow:
   clip` would hide the bottom row off the card edge. */
@media (min-width: 720px) {
    .results-card[data-engine-state="complete"] {
        display: flex;
        flex-direction: column;
        align-items: stretch;
    }
    .results-card[data-engine-state="complete"] .metric-col {
        /* Drop the grid-column constraint; the flex container hands
           the metric column the full card width. The padding-inline-
           start (≥720 grid) retires too so the complete-wrap centres
           against the card, not against the post-meter offset. */
        padding-inline-start: 0;
        align-items: center;
        text-align: center;
    }
    .results-card[data-engine-state="complete"] .metric-display {
        justify-content: center;
    }
}

/* AQ-P3 — REVERSAL of DDR AJ-W3-δ (complete-state `min-block-size:
   min-content`). The meter + results-card are ONE sized surface: the
   card holds the SAME `min-block-size: var(--chassis-max-block-size)`
   box at complete that it carries at idle / running, so the completion
   reveal is a CONTENT-SWAP inside a pre-sized surface — NOT a re-layout.

   WHY THE REVERSAL: AJ-W3-δ relaxed the clamp to `min-content` at
   complete to close the ~40% empty dead-band A3 §5.D-3 measured below
   the ResultStack. But that shrink IS the reveal's dominant CLS source:
   the card box re-sized 900→742px on the completion edge (`dy +49,
   dh -158`), and the parent `DIV.relative.z-content` wrapper re-flowed
   with it. Measured the worst single CLS in the app (~0.39 from the
   SECTION alone; ~0.54 total with the wrapper's companion shift). The
   dead-band close traded a cosmetic empty band for the app's worst
   layout shift — a bad trade once the reveal became a witnessed CWV
   surface (A6 G2 / production `useCompletionChoreography` edge).

   THE GESTALT: the dead-band is closed WITHOUT re-sizing the box. At
   complete the `.metric-col → .metric-display → .complete-wrap` flex
   chain (below) carries `flex: 1 1 auto; min-block-size: 0` and the
   `.complete-wrap` centres its content (`justify-content: center`), so
   the headline + rows sit CENTRED in the fixed-height card — balanced
   whitespace top + bottom rather than a bottom-heavy dead-band. The
   perceptual dead-band is closed by CENTRING in the reserved box, not
   by shrinking the box. ONE surface, two content states; the
   choreography swaps content + opacity and never touches the card box.

   RE-LITIGATE-IF: the chassis envelope itself changes such that the
   centred complete content reads as marooned in too much air (then
   close the band via an intrinsic `max-block-size` content clamp that
   does NOT shrink the OUTER card box — keep the reveal a content-swap).

   The `max-block-size: var(--chassis-max-block-size)` ceiling on the
   base `.results-card` already keeps a tall ResultStack inside the
   chassis envelope; with min == max the card is a fixed surface. */

/* The pure-flex chain below the card stays as the AC.W6d-era fix —
   `flex: 1; min-block-size: 0` makes each layer claim the residual
   card space cleanly. With `.results-card`'s min-block-size relaxed
   above, the chain now resolves against a shorter (content-sized)
   parent instead of the chassis envelope; the four-row panel still
   fits inside `max-block-size` and the ResultStack's own `flex: 1 1 0`
   stays the documented last-resort scroll fallback.

   For the running / idle states `.metric-display` is a single-cell GRID
   that stack-superimposes the pill-stack + complete-wrap. At complete
   the pill-stack is `display: none`, so the superposition is no longer
   needed: the display flips to `flex` column and each layer
   (`metric-col → metric-display → complete-wrap`) carries
   `flex: 1; min-block-size: 0`. */
.results-card[data-engine-state="complete"] .metric-col {
    flex: 1 1 auto;
    min-block-size: 0;
}
.results-card[data-engine-state="complete"] .metric-display {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-block-size: 0;
}
.results-card[data-engine-state="complete"] .complete-wrap {
    flex: 1 1 auto;
    min-block-size: 0;
}

/* Reduced-motion bracket: spatial choreography flattens to near-instant.
   End-state opacity 0 + translateX(-24%) on the dial is preserved (the
   layout still flips), but transitions collapse — no slide, no easing.
   The 220ms colour cross-fade on `.result-value` / `.result-unit` stays
   (WCAG 2.3 considers it accessible). */
@media (prefers-reduced-motion: reduce) {
    .results-card,
    .meter-col,
    .results-divider {
        transition-duration: 0.01ms;
    }
}

/* Phase progress: Progress's gradient variant carries `transition-all` on
   the indicator natively, so consumer-side transition rules are no longer
   needed. The .phase-progress class remains as a positioning hook
   (overrides handled inline via :style on the component). */

/* ─────────────────────── Completion panel ─────────────────────────────────
   The completed-test surface: golden complete logo + headline, a tapered
   hairline, then the stacked per-metric rows (ResultStack — one row per
   visible metric, 4 default / 2 dpi).

   Sizing — the complete-wrap fills the metric-col which at complete owns
   the full card width. The wrap must stay WITHIN the chassis budget the
   card clamps to (`max-block-size: var(--chassis-max-block-size)` +
   `overflow: clip`). The gap clamp is kept tight and the headline cluster
   + rows are sized so the four-row default panel fits the budget; on a
   short viewport the rows yield first (their own min-block-size relaxes).
─────────────────────────────────────────────────────────────────────── */
.complete-wrap {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    /* AJ-W3-μ.4 — consume `--celebration-row-rhythm` (the canonical
       complete-screen row rhythm, FD2 §5.4). Replaces the prior inline
       `clamp(0.5rem, 1.6cqi, 1rem)` formula with the named token so
       ThankYou + Speedtest complete share one tunable rhythm. The
       in-line fallback keeps the file self-contained. */
    gap: var(--celebration-row-rhythm, clamp(0.5rem, 1.6cqi, 1rem));
}

/* AV.W9.a — the `.complete-headline-cluster` row/stack composition, its
   `@container (min-width: 28rem)` row-flip, and the `.complete-headline-
   host` AN-D5 type-clamp carved into `<ResultHeadlineCluster>`'s scoped
   styles. They moved WITH the markup (same DOM position, same `@container`
   ancestor resolution) so the rendered cluster is byte-stable. The
   `.complete-reveal` reveal gate below STAYS here — it is the composition-
   root choreography gate, and it reaches the carved cluster's root via
   the parent's scoped `data-v-*` stamp (identical to how it reaches
   `<ResultStack class="complete-reveal …">`). */

/* AN-T1 — in-place REVEAL on `--spring-snappy`, gated on the morph
   clock's resolution. The retired `pane-slide` Transition was a
   parallel second channel layered on top of the morph (the morph
   delivered the box; pane-slide then slid the headline cluster in
   from the right). With the clock-owner pattern the morph IS the
   entrance — once it resolves (`data-morph-resolved="true"`), the
   reveal cascade lands IN-PLACE on the delivered box: a settled
   fade-up of the headline cluster, then the row stagger (owned by
   ResultStack's per-row TransitionGroup off the `useStagger`
   cadence), then the section divider draws (already in ResultStack).
   The two halves now read as one settle — the meter morphs into the
   plate, the plate then breathes its contents into place.

   `pane-slide` stays the canonical vocabulary for route/survey-step
   transitions; only THIS specific overlap with the morph retired. */
.complete-reveal {
    opacity: 0;
    transform: translateY(10px);
    /* Reveal is INERT until the morph clock resolves; the
       `[data-morph-resolved="true"]` lane below opens it. */
}
.results-card[data-morph-resolved="true"] .complete-reveal {
    opacity: 1;
    transform: translateY(0);
    transition:
        opacity var(--motion-duration-phase-handoff, 600ms) var(--ease-out),
        /* Use the snappy spring linear() curve for a settled arrival;
           the morph delivered the box on the BIG bouncy spring, the
           reveal LANDS on its tighter sibling — same family, crisp. */
        transform 900ms var(--spring-snappy);
}
/* The result-rows reveal cascades behind the headline; ResultStack's
   per-row TransitionGroup owns the actual stagger. The .complete-
   reveal--rows scope kept here so the host-level fade-up is the only
   per-rows entrance the wrap supplies — the rows' own
   TransitionGroup then layers the per-row decay on top. */
.results-card[data-morph-resolved="true"] .complete-reveal--rows {
    transition-delay: 110ms; /* DL begins after the seal lands */
}

/* PRM: under reduced-motion the morph composable skips the spine, the
   resolved handle flips immediately, and the reveal collapses to a
   near-instant opacity change (no translate). */
@media (prefers-reduced-motion: reduce) {
    .complete-reveal,
    .results-card[data-morph-resolved="true"] .complete-reveal {
        transform: none;
        transition: opacity 0.01ms linear;
    }
}

/* Per-row typography + transition rules live on ResultStack and
   PhaseTimeline. The shell rules above stay here because the
   `data-engine-state` visibility gate hooks at the composition root. */

/* ─────────────────────── Pulse aura adoption ─────────────────────────────
   The aura is a `<Pulse variant="aura">` whose `.pulse-aura` absolutely
   fills the host. Hosts here own `position: relative` and a
   border-radius for the halo shape.
───────────────────────────────────────────────────────────────────────── */

.idle-hero-host {
    position: relative;
    border-radius: 1rem;
}
.idle-hero-aura {
    --pulse-aura-color: var(--muted-foreground);
    --pulse-aura-opacity-min: 0.18;
    --pulse-aura-opacity-max: 0.36;
}
</style>
